import { useQuery } from "@tanstack/react-query";
import { type FormikValues, Formik } from "formik";
import lodashGet from "lodash/get";
import lodashSet from "lodash/set";
import React, {
  type ComponentPropsWithoutRef,
  useEffect,
  useState,
} from "react";
import PillarSelectComponent, {
  type PillarFormProfileSelectValueType,
  type PillarFormProfileSelectProps,
} from "shared/components/pillar-form/components/PillarSelectComponent";
import {
  type PillarTableBaseColumnCellValuePathProps,
  type PillarTableEditColumnProps,
} from "shared/components/pillar-table/columns/PillarTableColumn";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { type DeepKeys } from "shared/types/deepkeys";

const PillarTableColumnText = <T,>({
  cellValuePath,
}: PillarTableBaseColumnCellValuePathProps<T> &
  ComponentPropsWithoutRef<"div">) => {
  const rowContext = usePillarTableRowContext();
  const cellValue = lodashGet(rowContext?.rowValue, cellValuePath ?? "");

  return typeof cellValue === "string" || typeof cellValue === "number" ? (
    <>{cellValue}</>
  ) : (
    <></>
  );
};
const Edit = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>({
  cellValuePath,
  formValuePath,
  testid,
  ...props
}: PillarTableEditColumnProps<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric
> &
  ComponentPropsWithoutRef<"div">) => {
  // console.log("Rendered PillarTableColumnText.Edit", {
  //   cellValuePath,
  //   formValuePath,
  //   props,
  // });
  const tableContext = usePillarTableContext();
  const rowContext = usePillarTableRowContext<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >();
  const value = lodashGet(rowContext?.rowValue, cellValuePath ?? "");
  const [inputValue, setInputValue] = useState(value ?? "");
  return (
    <input
      type="text"
      value={inputValue as string}
      data-testid={
        testid && tableContext.testidTable
          ? `${tableContext.testidTable}-table-row-${rowContext.rowIndex}-column-${testid}-edit`
          : undefined
      }
      onChange={(e) => {
        setInputValue(e.target.value);
        rowContext.setFormValues!((prevValues) => {
          const newValues = { ...prevValues };
          lodashSet(
            newValues as object,
            formValuePath ?? cellValuePath!,
            e.target.value,
          );
          return newValues;
        });
      }}
    />
  );
};

export type PillarTableCellEditSelectMenuProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = {
  optionValuePath: DeepKeys<RowGeneric>;
  optionValueProperty: string;
  optionDisplayProperty: string;
  selectQuery: (() => Promise<RowGeneric[]>) | string;
} & PillarTableEditColumnProps<RowGeneric>;

const EditSelectMenu = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>({
  cellValuePath,
  formValuePath,
  optionValuePath,
  ...props
}: PillarTableCellEditSelectMenuProps<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric
>) => {
  const rowContext = usePillarTableRowContext<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >();

  const tableContext = usePillarTableContext();
  if (!rowContext) {
    throw new Error(
      "PillarTableCellEditSelectMenu must be used inside a PillarTableColumn",
    );
  }

  const optionValue = lodashGet(rowContext?.rowValue, optionValuePath ?? "");
  const [inputValue, setInputValue] = useState(optionValue as string);

  const selectOptions = useQuery({
    queryKey: ["select-options", cellValuePath, rowContext.inlineEditMode],
    queryFn: async () => {
      if (typeof props.selectQuery === "string") {
        return lodashGet(tableContext, props.selectQuery);
      }
      if (!props.selectQuery || rowContext.inlineEditMode !== "edit") {
        return [];
      }
      return props.selectQuery();
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  });
  return (
    <div>
      <select
        data-testid={
          props.testid ??
          `pillartable-table-inline-edit-select-${rowContext.rowIndex}`
        }
        className="pr-3"
        value={inputValue as string}
        onChange={(e) => {
          if (e.target.value) {
            rowContext.setFormValues!((prevValues) => {
              setInputValue(e.target.value as string);
              const newValues = { ...prevValues };
              lodashSet(
                newValues as object,
                formValuePath ?? "",
                e.target.value as string,
              );
              return newValues;
            });
          }
        }}
      >
        {selectOptions.data &&
          selectOptions.data.map((option: PillarTableSafeAny) => {
            return (
              <option
                key={`edit-cell-select-menu-${rowContext.rowIndex}-${option[props.optionValueProperty]}`}
                //TODO: This is brittle. In practice should be fine, but we should check for type validity to reduce unexpected runtime bugs.
                value={option[props.optionValueProperty] as string | number}
              >
                {String(option[props.optionDisplayProperty])}
              </option>
            );
          })}
      </select>
    </div>
  );
};

export type PillarTableCellEditSelectComponentProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
  SelectMenuEntityType = PillarTableSafeAny,
  SelectMenuValueType = PillarTableSafeAny,
> = {
  optionValuePath?: DeepKeys<RowGeneric>;
} & PillarTableEditColumnProps<RowGeneric> &
  Omit<
    PillarFormProfileSelectProps<
      SelectMenuEntityType,
      SelectMenuValueType,
      InlineEditFormGeneric
    >,
    "presetValueCallback" | "name" | "onChangeCallback"
  > & {
    presetValueCallback?: (
      value:
        | PillarFormProfileSelectValueType<SelectMenuValueType>
        | PillarFormProfileSelectValueType<SelectMenuValueType>[],
      rowValue: RowGeneric,
    ) => Promise<PillarFormProfileSelectValueType<SelectMenuValueType>[]>;
    selectedValuesKey?: DeepKeys<InlineEditFormGeneric>;
  };

const EditSelectComponent = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric extends FormikValues = FormikValues,
  SelectMenuEntityType = PillarTableSafeAny,
  SelectMenuValueType = PillarTableSafeAny,
>({
  optionValuePath,
  presetValueCallback,
  isMultiple,
  formValuePath,
  selectedValuesKey,
  testid,
  ...restProps
}: PillarTableCellEditSelectComponentProps<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric,
  SelectMenuEntityType,
  SelectMenuValueType
>) => {
  const rowContext = usePillarTableRowContext<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >();
  if (!rowContext) {
    throw new Error(
      "PillarTableCellEditSelectMenu must be used inside a PillarTableColumn",
    );
  }

  const optionValue = lodashGet(rowContext?.rowValue, optionValuePath ?? "");
  useEffect(() => {
    rowContext.setFormValues((prevValues) => {
      const updatedValues = { ...prevValues };

      lodashSet(updatedValues as object, formValuePath!, optionValue);

      return updatedValues as InlineEditFormGeneric;
    });
  }, [optionValue]);
  return (
    <Formik<{
      internalForm: SelectMenuValueType;
    }>
      enableReinitialize
      initialValues={{
        internalForm: optionValue as unknown as SelectMenuValueType,
      }}
      onSubmit={() => undefined}
    >
      {(formikContext) => (
        <PillarSelectComponent<
          SelectMenuEntityType,
          SelectMenuValueType,
          {
            internalForm: SelectMenuValueType;
          }
        >
          {...restProps}
          name={"internalForm"}
          presetValueCallback={
            presetValueCallback
              ? async (value) => presetValueCallback(value, rowContext.rowValue)
              : undefined
          }
          testid={
            testid
              ? `pillartable-table-inline-edit-select-${rowContext.rowIndex}-${testid}`
              : undefined
          }
          onChangeCallback={(newValue, actionMeta, queryResults) => {
            rowContext.setFormValues((prevValues) => {
              const updatedValues = { ...prevValues };
              const resolvedValue = isMultiple
                ? Array.isArray(newValue)
                  ? newValue.map((option) => option.value)
                  : []
                : newValue && "value" in newValue
                  ? newValue.value
                  : null;

              lodashSet(updatedValues as object, formValuePath!, resolvedValue);

              if (selectedValuesKey) {
                lodashSet(
                  updatedValues as object,
                  selectedValuesKey,
                  newValue ?? null,
                );
              }

              return updatedValues as InlineEditFormGeneric;
            });
          }}
        />
      )}
    </Formik>
  );
};
EditSelectMenu.isEditComponent = true;
PillarTableColumnText.EditSelectMenu = EditSelectMenu;
Edit.isEditComponent = true;
PillarTableColumnText.Edit = Edit;
EditSelectComponent.isEditComponent = true;
PillarTableColumnText.EditSelectComponent = EditSelectComponent;
export default PillarTableColumnText;
